import { formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { useEffect, type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { ChartSyncProvider } from "../../components/index.ts";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { useDataQuality } from "../../state/data-quality.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import {
  useIdentityMcpSummary,
  useIdentityMcpDenials,
  type IdentityMcpSummary,
  type IdentityMcpWindow,
} from "./use-identity-mcp.ts";
import { OptInBanner } from "./OptInBanner.tsx";
import { OperationsTable } from "./OperationsTable.tsx";
import { LatencyTiles } from "./LatencyTiles.tsx";
import { DenialsChart } from "./DenialsChart.tsx";
import { DenialsTable } from "./DenialsTable.tsx";
import { ProtectedCorePanel } from "./ProtectedCorePanel.tsx";
import "./identity-mcp.css";

/**
 * The Identity MCP screen (task 7.3; requirement 13.3, 13.6; design D1/D5). It composes the regions
 * requirement 13.3 calls for over two REST reads, both role `viewer`, taken straight from the
 * generated `@oathera/contract/api` with no stub data (requirement 13.6):
 *
 * - `GET /api/v1/identity-mcp/summary` → the PRIMARY snapshot the whole screen rests on: the opt-in
 *   coverage banner (registered/reporting/dark, "dark = off on purpose"), operations by tool and
 *   outcome, the p50/p95/p99 latency percentiles, and Protected_Core status per instance linked to
 *   `/integrity`.
 * - `GET /api/v1/identity-mcp/denials` → denials by reason, drawn stacked over time and listed with
 *   exact per-reason totals. The denials are a SECONDARY region that shows its own loading/empty
 *   state inside the ready screen so a slow or empty denials read never blanks the page.
 *
 * Both windows are resolved from the shared time-range, so pausing freezes every query at once
 * (design 12.2, FR-13 AC2). The summary drives the scaffold state. The screen reports the summary's
 * `meta.data_quality` into the persistent data-quality strip, the same way the System/Overview
 * screens do, so "a chart is never read without knowing what might be missing" (NFR-4).
 *
 * It handles every state a screen must (design 12.3):
 * - restricted: the summary is viewer-level; an unknown/below-viewer role sees the restricted state.
 * - loading / error / empty / ready as the summary query resolves; a cold-core waking error defers
 *   to the shell's banner (kept "loading" under it so there is no contradiction).
 */
export function IdentityMcpScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();

  const { query: summary, window } = useIdentityMcpSummary();
  const { query: denials } = useIdentityMcpDenials();

  const state = resolveState(role, summary);

  return (
    <ScreenScaffold
      title="Identity MCP"
      subtitle="Opt-in coverage, operations by tool and outcome, denials by reason over time, latency percentiles and Protected_Core status per instance."
      state={state}
    >
      {summary.data !== undefined && (
        <IdentityMcpBody
          summary={summary.data}
          window={window}
          live={live}
          denials={denials}
        />
      )}
    </ScreenScaffold>
  );
}

/** Decide which scaffold state to show from the role and the summary query status. */
function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  query: ReturnType<typeof useIdentityMcpSummary>["query"],
): ScreenState {
  // Least privilege: an unknown or below-viewer role sees the restricted state, not an error.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the Identity MCP view is hidden until it is. The server enforces access regardless."
          : "The Identity MCP view needs at least the viewer role.",
    };
  }

  if (query.data !== undefined) {
    if (isSummaryEmpty(query.data)) {
      return {
        kind: "empty",
        cause:
          "No Identity MCP activity was recorded in this window. Identity MCP is opt-in — widen the time range, or check that an Identity MCP source is registered and configured to report.",
      };
    }
    return { kind: "ready" };
  }

  if (query.isError) {
    // The cold-core waking error is the shell's banner; keep "loading" under it (no contradiction).
    if (query.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: errorMessage(query.error) };
  }

  return { kind: "loading" };
}

/**
 * The summary is "empty" when no source is registered, no operation was counted, and no instance
 * reported a Protected_Core state. A registered-but-dark source still renders the banner (it is a
 * meaningful, non-empty coverage fact), so a tenant that has opted in but is quiet does not read as
 * "nothing to show".
 */
function isSummaryEmpty(data: IdentityMcpSummary): boolean {
  const noRegistered = data.opt_in.sources_registered === 0;
  const noOps = data.operations.every((o) => (o.count.value ?? 0) === 0);
  const noCore = data.core_state.length === 0;
  return noRegistered && noOps && noCore;
}

function errorMessage(error: ReturnType<typeof useIdentityMcpSummary>["query"]["error"]): string {
  if (error === null) return "The Identity MCP view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see Identity MCP data. The server enforces access.";
    case "network":
      return "The Identity MCP request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function IdentityMcpBody(props: {
  summary: IdentityMcpSummary;
  window: IdentityMcpWindow;
  live: boolean;
  denials: ReturnType<typeof useIdentityMcpDenials>["query"];
}): ReactNode {
  const { summary } = props;
  const generatedAt = summary.meta.generated_at;

  // Keep the persistent data-quality strip consistent with this snapshot (design 12.2, NFR-4), the
  // same way the Overview/System screens do. Only the fields the snapshot carries are reported.
  const { report } = useDataQuality();
  const dq = summary.meta.data_quality;
  const gapCount = (dq.gaps ?? []).length;
  const dropsInRange = dq.drops_in_range;
  const samplingInEffect = dq.sampling_in_effect;
  useEffect(() => {
    report({
      drops: { atSource: 0, atEdge: 0, inPipeline: dropsInRange },
      sampling: { active: samplingInEffect, rate: null, exact: !samplingInEffect },
      hasSequenceGap: gapCount > 0,
    });
  }, [report, dropsInRange, samplingInEffect, gapCount]);

  return (
    <div className="imcp" data-live={props.live}>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="imcp__meta">
          {props.live ? "Live" : "Paused"} · window{" "}
          <time dateTime={isoAttr(props.window.fromMs)}>{formatTimeOfDay(props.window.fromMs)}</time>{" "}
          to{" "}
          <time dateTime={isoAttr(props.window.toMs)}>{formatTimeOfDay(props.window.toMs)}</time> ·
          generated{" "}
          <time dateTime={isoAttr(generatedAt)}>{formatTimeOfDay(generatedAt)}</time>
        </p>
      </div>

      {/* Opt-in coverage comes first: it frames every number below (dark = off on purpose). */}
      <OptInBanner optIn={summary.opt_in} />

      <OperationsTable operations={summary.operations} />

      <LatencyTiles summary={summary} />

      {/* Denials is a secondary region: it shows its own state so a slow/empty read never blanks the
          page. The stacked chart joins a sync group of its own. */}
      <DenialsRegion denials={props.denials} />

      <ProtectedCorePanel coreState={summary.core_state} />
    </div>
  );
}

/**
 * The denials region (chart + table). It renders its own honest loading status while the first
 * denials fetch is in flight and a non-blocking error note when the (non-waking) read failed, so one
 * slow or failing secondary read never takes down the whole screen.
 */
function DenialsRegion(props: {
  denials: ReturnType<typeof useIdentityMcpDenials>["query"];
}): ReactNode {
  const { denials } = props;
  const isLoading = denials.data === undefined && denials.isFetching;
  const isError = denials.isError && !(denials.error?.isWaking ?? false);

  if (isLoading) {
    return (
      <div className="imcp-secondary-state" role="status" aria-live="polite" aria-label="Denials">
        <strong>Loading denials…</strong>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="imcp-secondary-state" data-kind="error" role="alert" aria-label="Denials">
        <strong>Denials unavailable</strong>
        <span>Denials by reason could not load for this window.</span>
      </div>
    );
  }
  if (denials.data === undefined) return null;

  return (
    <section className="imcp-denials-region" aria-label="Denials by reason over time">
      <ChartSyncProvider>
        <DenialsChart items={denials.data.items} />
      </ChartSyncProvider>
      <DenialsTable items={denials.data.items} />
    </section>
  );
}
